/**
 * The walkthrough overlay itself (doc 3 E1-E8, E13, H4, H5, H8; addendum R4, R5, §8).
 * Radix Dialog gives role="dialog", the focus trap and Esc, and hides the page underneath;
 * aria-modal is set here (Radix 1.1 doesn't render it), and useReturnFocus gives focus back
 * to the opener; the header "How Fomo-Fi works · N of 10" is real text and labels the
 * dialog. All of it is checked against the real dialog in walkthroughOverlayReal.test.tsx.
 *
 * Layout, from Anthony's Sign-up Screens prototype (desktop) and the How It Works phone
 * canvas (phone look only):
 * - Desktop: an 860 px card. Header and ×, ten segments, the picture on the left and the
 *   words on the right (E4); footer "Back to [page]" on the left, Back and Next on the
 *   right (E6, E7).
 * - Phones (below sm): full screen (§8). The header is "How Fomo-Fi works · N of 10"
 *   between 44 px previous and next arrows, then the segments, the picture ABOVE the
 *   words (E5), and the footer pinned in reach (Paul's phone behaviour: sticky main
 *   button, no sideways scroll).
 * - The × is named like "Back to [page]", which does the same (R5). On step 10, Next
 *   reads "Done · back to [page]" (R4) and finishes; Back is disabled on step 1, in the
 *   prototype's grey.
 */
import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import {
  WALKTHROUGH_BACK_COPY,
  WALKTHROUGH_NEXT_COPY,
  WALKTHROUGH_NEXT_STEP_LABEL_COPY,
  WALKTHROUGH_PREVIOUS_STEP_LABEL_COPY,
  WALKTHROUGH_STEP_COUNT,
  WALKTHROUGH_STEPS_COPY,
  walkthroughBackToCopy,
  walkthroughDoneCopy,
  walkthroughHeaderCopy,
} from "@fomofi/shared";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { canGoBack, clampStep } from "@/lib/walkthrough";
import { WalkthroughPicture } from "./WalkthroughPictures";

/** The phone header's arrows: 44 px at any text size (px, not rem, so 200% text leaves the
 *  count its room), phone only (the footer's Back and Next serve the
 *  desktop card). On step 1 and step 10 the missing arrow keeps its space. */
const ARROW =
  "flex size-[44px] shrink-0 items-center justify-center rounded-full text-foreground hover:bg-muted sm:hidden";
const ARROW_SPACER = "size-[44px] shrink-0 sm:hidden";

/** "How Fomo-Fi works · N of 10", the shared words, with "N of 10" kept on one line: at
 *  360 px and 200% text the header wrapped as "· 1 of / 10". The text (and so the
 *  dialog's name) is unchanged. */
function HeaderText({ step }: { step: number }) {
  const text = walkthroughHeaderCopy(step);
  const at = text.lastIndexOf(" · ") + 3;
  return (
    <>
      {text.slice(0, at)}
      <span className="whitespace-nowrap" data-walkthrough-count="">
        {text.slice(at)}
      </span>
    </>
  );
}

export function WalkthroughDialog({
  initialStep,
  from,
  onClose,
}: {
  initialStep: number;
  from: string;
  /** Called once, with the step showing when it closed, and whether it was finished
   *  (step 10's "Done · back to [page]") rather than closed. */
  onClose: (step: number, finished: boolean) => void;
}) {
  const [step, setStep] = useState(() => clampStep(initialStep));
  const copy = WALKTHROUGH_STEPS_COPY[step - 1];
  const last = step === WALKTHROUGH_STEP_COUNT;
  const backTo = walkthroughBackToCopy(from);
  const back = () => setStep((s) => clampStep(s - 1));
  const next = () => setStep((s) => clampStep(s + 1));
  // A new step starts at its top: at 200% text a step scrolls, and the scroller (the
  // dialog itself) kept the last step's position, opening the next one mid-picture.
  const scroller = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scroller.current?.scrollTo?.({ top: 0 });
  }, [step]);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose(step, false)}>
      <DialogContent
        ref={scroller}
        // H4 (DEF-026): Radix's modal dialog hides the page (aria-hidden) and traps focus
        // but renders no aria-modal; doc 3 names it, so it's said outright.
        aria-modal="true"
        showCloseButton={false}
        className={cn(
          "w-full grid-cols-[minmax(0,1fr)] gap-0 sm:max-w-[860px] sm:rounded-3xl",
          // §8: full screen on phones, with the safe areas kept clear.
          "max-sm:top-0 max-sm:left-0 max-sm:h-dvh max-sm:max-h-dvh max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:grid-rows-[auto_auto_1fr_auto] max-sm:rounded-none max-sm:border-0",
        )}
        data-testid="walkthrough"
      >
        <div className="flex items-center justify-between gap-1 bg-background px-3 pt-[max(env(safe-area-inset-top),0.5rem)] max-sm:sticky max-sm:top-0 max-sm:z-10 sm:gap-3 sm:px-7 sm:pt-6">
          {step > 1 ? (
            <button
              type="button"
              aria-label={WALKTHROUGH_PREVIOUS_STEP_LABEL_COPY}
              onClick={back}
              className={ARROW}
            >
              <ChevronLeft className="h-5 w-5" aria-hidden />
            </button>
          ) : (
            <span className={ARROW_SPACER} aria-hidden />
          )}
          <DialogTitle className="min-w-0 flex-1 text-center text-[13px] font-semibold text-primary sm:text-left sm:text-sm">
            <HeaderText step={step} />
          </DialogTitle>
          {!last ? (
            <button
              type="button"
              aria-label={WALKTHROUGH_NEXT_STEP_LABEL_COPY}
              onClick={next}
              className={ARROW}
            >
              <ChevronRight className="h-5 w-5" aria-hidden />
            </button>
          ) : (
            <span className={ARROW_SPACER} aria-hidden />
          )}
          <DialogClose asChild>
            <button
              type="button"
              aria-label={backTo}
              data-walkthrough-close=""
              className="flex size-[44px] shrink-0 items-center justify-center rounded-full bg-muted text-foreground hover:bg-muted/80"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </DialogClose>
        </div>

        {/* Side padding is in px, not rem: at 200% text rem padding ate a third of a 360 px
            phone and squeezed the pictures' names to a few letters a line. */}
        {/* E3: ten segments; done and current are blue. The text above carries the
            position, so the bar is decoration (H8). */}
        <div className="grid grid-cols-10 gap-1 px-[20px] pt-3 sm:px-7 sm:pt-4" aria-hidden>
          {Array.from({ length: WALKTHROUGH_STEP_COUNT }, (_, i) => (
            <span
              key={i}
              data-done={i < step}
              className={cn("h-1.5 rounded-full", i < step ? "bg-primary" : "bg-muted")}
            />
          ))}
        </div>

        <div className="flex flex-col gap-5 px-[20px] py-5 sm:flex-row sm:flex-wrap sm:items-center sm:gap-[26px] sm:px-7 [&>*]:min-w-0">
          {/* E5: on phones the picture sits above the words. */}
          <div
            data-testid="walkthrough-picture"
            className="flex items-center rounded-[18px] border border-border bg-muted/50 px-[16px] py-4 sm:min-h-[280px] sm:flex-[1_1_340px] sm:p-[18px]"
          >
            <WalkthroughPicture step={step} />
          </div>
          <div className="flex flex-col gap-3 [overflow-wrap:anywhere] sm:flex-[1_1_300px]">
            <span className="self-start rounded-full bg-primary/10 px-3 py-1 text-[13px] font-semibold text-primary">
              {copy.tag}
            </span>
            <h3 className="text-[26px] leading-tight font-bold tracking-tight text-balance">
              {copy.title}
            </h3>
            <DialogDescription className="text-[15px] leading-relaxed text-muted-foreground sm:text-base sm:leading-[1.6]">
              {copy.body}
            </DialogDescription>
          </div>
        </div>

        {/* Sticky, so Next stays in reach while a tall step (200% text, a short phone)
            scrolls (Paul's phone rule: a sticky main button). */}
        <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-3 border-t border-border/60 bg-background px-[20px] pt-4 pb-[max(env(safe-area-inset-bottom),1rem)] sm:px-7 sm:pb-6">
          <DialogClose asChild>
            <Button
              variant="link"
              className="h-auto min-h-11 w-full min-w-0 shrink justify-start gap-1 px-0 text-left text-[15px] font-semibold whitespace-normal sm:w-auto"
            >
              <ChevronLeft className="h-4 w-4 shrink-0" />
              {backTo}
            </Button>
          </DialogClose>
          <div className="grid w-full min-w-0 grid-cols-2 gap-2.5 sm:flex sm:w-auto">
            <Button
              variant="outline"
              className="h-auto min-h-12 shrink rounded-xl px-[18px] text-[15px] whitespace-normal disabled:text-walkthrough-disabled disabled:opacity-100"
              disabled={!canGoBack(step)}
              onClick={back}
            >
              {WALKTHROUGH_BACK_COPY}
            </Button>
            {last ? (
              <Button
                className="h-auto min-h-12 shrink rounded-xl px-[22px] text-[15px] font-semibold whitespace-normal"
                onClick={() => onClose(WALKTHROUGH_STEP_COUNT, true)}
              >
                {walkthroughDoneCopy(from)}
              </Button>
            ) : (
              <Button
                className="h-auto min-h-12 shrink rounded-xl px-[22px] text-[15px] font-semibold whitespace-normal"
                onClick={next}
              >
                {WALKTHROUGH_NEXT_COPY}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
